// TODO: Create a Cobra field where to put the Strapi ID of the responsible person, so is possible to fetch image data.

// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import List from '@mui/material/List';
import Section from '@/components/common/Section';
import ListItem from '@mui/material/ListItem';
import FadeInView from '@/components/common/FadeInView';
import ListItemText from '@mui/material/ListItemText';
import ComponentBlocksHero from '@/components/strapi/Blocks/ComponentBlocksHero';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DropdownAccordionBlock from '@/components/strapi/Blocks/ComponentBlocksDropdownAccordionBlock';
import InfoCard, { type InfoCardProps } from '@/components/common/InfoCard';
import { PersonCard, type PersonCardModel } from '@/components/common/PersonCards';

// ** Utils
import { useGetPersonById } from '@/lib/utils/personDataUtils';
import { useGetEventData } from '@/lib/utils/eventDataUtils';
import { handleEventImage, handleGetResponsibleData } from '@/utils/domain/eventUtils';

// ** Types
import type { User } from 'next-auth';
import type { Event } from '@/types/graphql';
import type { Provider } from 'next-auth/providers';
import type { UploadFile } from '@/types/graphql';
import type { EventDetailViewProps } from './EventDetailView.types';
import type { EventDetailBlockProps } from '@/features/event-detail/blocks/event-detail-block';

const sectionHasCopy = (copy: React.ReactNode): boolean => {
	if (copy == null || copy === false) return false;
	if (typeof copy === 'string') return copy.trim().length > 0;
	return true;
};

const mapApiToInfoCard = ({ apiData }: { apiData: Event }): InfoCardProps => {
	// 1. Get the unique identifier for the URL (encoded for safety)
	const eventId = encodeURIComponent(apiData.eventNumber as string);

	// 2. Define the absolute base path for this specific event
	const eventBasePath = `/veranstaltungen/${eventId}`;

	const renderPrice = (price: number | null, label: string) => {
		if (price === null || price === 0) {
			return (
				<React.Fragment>
					{label}{' '}
					<Typography component="span" variant="body-sm" sx={{ fontWeight: 700 }}>
						Kostenlos
					</Typography>
				</React.Fragment>
			);
		}
		return (
			<React.Fragment>
				{label}{' '}
				<Typography component="span" variant="body-sm" sx={{ fontWeight: 700 }}>
					{price} €
				</Typography>
				{!apiData.priceWithoutTax && ' zzgl. MwSt'}
			</React.Fragment>
		);
	};

	return {
		title: (apiData.eventNumber as string) ?? 'Keine Nummer',
		subtitle: `ReferentIn: ${apiData.line ?? 'N/A'}`,
		details: [
			{
				icon: 'mdi:calendar-blank-outline',
				label: apiData.appointmentInfo ?? 'Termin folgt'
			},
			{
				icon: 'mdi:clock-outline',
				label: apiData.timeOfDay ?? 'Zeit nicht angegeben'
			},
			{
				icon: 'mdi:map-marker',
				label: apiData.eventPlace ?? 'Ort folgt'
			}
		],
		actions: [
			{
				label: 'Buchen',
				variant: 'dark',
				href: `${eventBasePath}/book`,
				priceLabel: renderPrice(apiData.memberPrice, 'Mitglieder')
			},
			{
				label: 'Buchen',
				variant: 'light',
				href: `${eventBasePath}/book-guest`,
				priceLabel: renderPrice(apiData.notMemberPrice, 'Nichtmitglieder')
			}
		],
		notes: [
			{
				id: 'memberFeeNote',
				title: 'Preishinweis',
				copy: apiData.memberFeeNote || ''
			}
		]
	};
};

const mapApiToEventProps = (apiData: Event, strapiPerson?: any): EventDetailBlockProps => {
	const sectors = strapiPerson?.sector ? JSON.parse(strapiPerson.sector) : [];

	return {
		textSections: [
			{
				id: 'zielgruppe',
				title: 'Zielgruppe',
				copy: apiData.groupTarget || ''
			},
			{
				id: 'ziel',
				title: 'Ziel',
				copy: apiData.target || apiData.description || ''
			},
			{
				id: 'inhalte',
				title: 'Inhalte',
				copy: (() => {
					if (!apiData.content) return '';

					// 1. Normalize line endings (\r\n or standalone \r into standard \n)
					const normalizedText = apiData.content.replace(/\r\n|\r/g, '\n');

					// 2. Split into individual lines and remove completely empty lines
					const lines = normalizedText.split('\n').filter((line: string) => line.trim() !== '');

					if (lines.length === 0) return '';

					// 3. Parse lines into structured blocks to preserve exact chronological order
					type Block = { type: 'text'; content: string } | { type: 'list'; items: string[] };
					const blocks: Block[] = [];

					lines.forEach((line: string) => {
						const trimmed = line.trim();
						// Regex detects if line starts with common bullet indicators: -, *, •, or numbers like 1.
						const isBullet = /^(?:[-*•]|\d+\.)\s+/.test(trimmed);
						// Strip the bullet indicator out for cleaner rendering
						const cleanContent = trimmed.replace(/^(?:[-*•]|\d+\.)\s+/, '');

						if (isBullet) {
							// If the last block is already a list, group this item into it
							const lastBlock = blocks[blocks.length - 1];
							if (lastBlock && lastBlock.type === 'list') {
								lastBlock.items.push(cleanContent);
							} else {
								blocks.push({ type: 'list', items: [cleanContent] });
							}
						} else {
							// Otherwise, treat it as a standalone paragraph block
							blocks.push({ type: 'text', content: trimmed });
						}
					});

					// 4. Render blocks sequentially, respecting text -> list -> text flow
					return (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
							{blocks.map((block) => {
								if (block.type === 'list') {
									return (
										<List key={`list-${block.items.join('-')}`} disablePadding>
											{block.items.map((item) => (
												<ListItem
													key={`item-${item}`}
													disablePadding
													disableGutters
													sx={{ alignItems: 'flex-start', mb: 0.5 }}
												>
													<FiberManualRecordIcon
														sx={{
															fontSize: '6px',
															mt: '10px',
															mr: 1.5,
															color: 'text.secondary',
															flexShrink: 0
														}}
													/>
													<ListItemText
														primary={item}
														sx={{ m: 0 }}
														slotProps={{
															primary: {
																variant: 'body-md',
																sx: { color: 'text.secondary', lineHeight: 1.5 }
															}
														}}
													/>
												</ListItem>
											))}
										</List>
									);
								}

								return (
									<Typography
										key={`text-${block.content}`}
										variant="body-md"
										sx={{ color: 'text.secondary', lineHeight: 1.5 }}
									>
										{block.content}
									</Typography>
								);
							})}
						</Box>
					);
				})()
			},
			{
				id: 'annotation',
				title: 'Hinweise zur Veranstaltung',
				copy: apiData.annotation || ''
			}
		],
		infoCard: {
			title: apiData.title ?? '',
			subtitle: apiData.appointmentInfo ?? '',
			location: apiData.eventPlace ?? '',
			details: [
				{
					icon: 'mdi:calendar-blank-outline',
					label: apiData.appointmentInfo ?? ''
				},
				{ icon: 'mdi:map-marker', label: apiData.eventPlace ?? '' }
			]
		},
		contactTitle: 'Haben Sie Fragen?',
		person: {
			id: strapiPerson?.id ?? apiData.responsible?.id ?? 'unknown',
			name: apiData.line?.split(',')[0] || '',
			roleLines: sectors.length > 0 ? sectors : [apiData.line?.split(',')[1]?.trim() || ''],
			team: 'organisation',
			imageData: strapiPerson?.profilePicture as UploadFile
		},
		defaultExpandedAccordionIds: ['ziel']
	};
};

const EventDetailView = ({
	id,
	host,
	locale,
	providers,
	containerSx,
	currentUser,
	newsBasePath,
	eventsBasePath,
	userBasePath,
	contactTitle = 'Haben Sie Fragen?',
	textSectionTitleSx,
	allowMultipleExpanded = true,
	defaultExpandedAccordionIds
}: EventDetailViewProps) => {
	const theme = useTheme();
	const gapMobileStack = theme.spacing(8);
	const contactHeadingIdMobile = React.useId();
	const contactHeadingIdDesktop = React.useId();

	const { eventById, loading } = useGetEventData({ eventId: id });

	const rawResponsible = eventById?.responsible;
	const responsible = handleGetResponsibleData(rawResponsible);

	const { data } = useGetPersonById({
		id: rawResponsible?.id,
		locale: locale
	});

	const mappedData = React.useMemo(() => {
		if (!eventById || loading) return null;
		return mapApiToEventProps(eventById);
	}, [eventById, loading]);

	const activeTextSections = React.useMemo(() => {
		return mappedData?.textSections?.length ? mappedData.textSections : [];
	}, [mappedData]);

	const infoCardData = React.useMemo(() => {
		if (!eventById) return null;
		return mapApiToInfoCard({
			apiData: eventById
		});
	}, [eventById]);

	const sectors = typeof data?.sector === 'string' ? JSON.parse(data.sector) : [];

	const person: PersonCardModel = {
		id: eventById?.responsible?.id ?? 'unknown-id',
		name: responsible.profile ?? 'Name nicht angegeben',
		roleLines: sectors,
		phone: responsible.phone ?? '',
		email: responsible.email ?? '',
		imageSrc: data?.profilePicture ? undefined : '/images/default-avatar.png', // TODO: Set a placeholder
		imageAlt: responsible.profile ?? 'Profilbild',
		team: 'organisation',
		imageData: data?.profilePicture as unknown as UploadFile // TODO Fix the type
	};

	const gapBetweenColumns = {
		md: theme.spacing(10),
		lg: theme.spacing(20)
	};
	const gapTextBlocksMd = theme.spacing(20);
	const gapInfoToContact = theme.spacing(13.75);

	const headingTitleSx = React.useMemo(
		() => ({
			color: theme.palette.primary.main,
			mb: theme.spacing(6),
			[theme.breakpoints.up('md')]: {
				fontSize: '2rem',
				lineHeight: '2.375rem',
				letterSpacing: '-0.64px'
			}
		}),
		[theme]
	);

	const validSections = React.useMemo(
		() => activeTextSections.filter((s) => Boolean(s.title?.trim()) && sectionHasCopy(s.copy)),
		[activeTextSections]
	);

	const accordionItems = React.useMemo(
		() =>
			validSections.map((s) => ({
				id: s.id,
				title: s.title,
				copy: s.copy
			})),
		[validSections]
	);

	const defaultAccordionIds = defaultExpandedAccordionIds ?? (accordionItems.length > 0 ? [accordionItems[0].id] : []);

	const renderContactSection = (headingId: string) => (
		<Box component="section" aria-labelledby={headingId}>
			<Typography id={headingId} component="h2" variant="title-sm" sx={headingTitleSx}>
				{contactTitle}
			</Typography>
			<Box
				component="ul"
				sx={{
					listStyle: 'none',
					p: 0,
					m: 0,
					width: '100%',
					display: 'flex',
					justifyContent: { xs: 'stretch', md: 'flex-start' }
				}}
			>
				{!loading && <PersonCard {...person} />}
			</Box>
		</Box>
	);

	const sidebarStack = (
		<Box sx={{ display: 'flex', flexDirection: 'column' }}>
			{infoCardData && <InfoCard {...infoCardData} />}
			<Box sx={{ mt: gapInfoToContact }}>{renderContactSection(contactHeadingIdDesktop)}</Box>
		</Box>
	);

	const eventImage = handleEventImage(eventById?.field || 'Seminar');

	if (loading) return null; // TODO: Add skeleton for this component

	return (
		<React.Fragment>
			<ComponentBlocksHero
				__typename="ComponentBlocksHero"
				host={host}
				copy={null}
				locale={locale}
				eyebrow={null}
				subline={null}
				showMenu={true}
				headline={eventById?.title ?? ''}
				heroVariant="Content"
				currentUser={currentUser as User}
				providers={providers as unknown as Provider}
				newsBasePath={newsBasePath ?? ''}
				eventsBasePath={eventsBasePath ?? ''}
				userBasePath={userBasePath ?? ''}
				segmentOverrides={[eventById?.title ?? '']}
				heroImage={
					{
						url: eventImage,
						alternativeText: eventById?.title ?? 'Hero Image'
					} as UploadFile
				}
			/>

			<Section containerSx={containerSx}>
				<FadeInView>
					{/* Mobile: Info → accordions → contact */}
					<Stack spacing={gapMobileStack} sx={{ display: { xs: 'flex', md: 'none' } }}>
						{infoCardData && <InfoCard {...infoCardData} />}

						{accordionItems.length > 0 ? (
							<DropdownAccordionBlock
								items={accordionItems}
								allowMultipleExpanded={allowMultipleExpanded}
								defaultExpandedIds={defaultAccordionIds}
								disableAutoAdvance
								titleColor="primary"
							/>
						) : null}

						{renderContactSection(contactHeadingIdMobile)}
					</Stack>

					{/* Desktop: two columns, or sidebar only when there are no text sections */}
					{validSections.length > 0 ? (
						<Box
							sx={{
								display: { xs: 'none', md: 'grid' },
								gridTemplateColumns: '1fr 1fr',
								columnGap: gapBetweenColumns,
								alignItems: 'start',
								width: '100%'
							}}
						>
							<Stack spacing={gapTextBlocksMd} sx={{ minWidth: 0 }}>
								{validSections.map((section) => (
									<Box key={section.id} component="section" aria-labelledby={`event-detail-text-${section.id}`}>
										<Typography
											id={`event-detail-text-${section.id}`}
											component="h2"
											variant="title-sm"
											sx={[
												headingTitleSx,
												...(textSectionTitleSx != null
													? Array.isArray(textSectionTitleSx)
														? textSectionTitleSx
														: [textSectionTitleSx]
													: [])
											]}
										>
											{section.title}
										</Typography>

										{typeof section.copy === 'string' ? (
											<Typography variant="body-md" component="div" sx={{ color: 'text.secondary' }}>
												{section.copy}
											</Typography>
										) : (
											<Box
												sx={{
													typography: 'body-md',
													color: 'text.secondary'
												}}
											>
												{section.copy}
											</Box>
										)}
									</Box>
								))}
							</Stack>

							<Box sx={{ minWidth: 0, width: '100%' }}>{sidebarStack}</Box>
						</Box>
					) : (
						<Box
							sx={{
								display: { xs: 'none', md: 'block' },
								maxWidth: 400,
								mx: 'auto',
								width: '100%'
							}}
						>
							{sidebarStack}
						</Box>
					)}
				</FadeInView>
			</Section>
		</React.Fragment>
	);
};

export default EventDetailView;
